import { IPartnerData } from 'legacy/lut/features/partner360/configuration/models/IPartnerData'
import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration/selectors/selectPartner360Data'
import { fetchPartner360Configuration } from 'legacy/lut/services/partner360/configuration/fetchPartner360Configuration'
import { map621ToConfigurationData } from 'legacy/lut/services/partner360/configuration/mappings/map621ToConfigurationData'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import Logger from 'core/utils/Logger'
import { selectCockpitPartnerData } from 'partner360/features/financialCockpit/model/selectors/selectCockpitPartnerData'

export const useKeyInitiativePartnerMarket = (partnerId: string | undefined, fYear: number) => {
  const cockpitPartners = useSelector(selectCockpitPartnerData)
  const partnerConfiguration = useSelector(selectPartner360Data)
  const [requestedConfiguration, setRequestedConfiguration] = useState<IPartnerData | null>(null)

  const cockpitPartner = useMemo(
    () =>
      cockpitPartners.find((partner) => partner.PartnerId === partnerId && partner.FYear === fYear),
    [cockpitPartners, partnerId, fYear]
  )
  const cachedConfiguration =
    partnerConfiguration.partnerId === partnerId && partnerConfiguration.fYear === fYear
      ? partnerConfiguration
      : null
  const loadedConfiguration =
    requestedConfiguration?.partnerId === partnerId && requestedConfiguration?.fYear === fYear
      ? requestedConfiguration
      : null
  const cachedMarketShort =
    cockpitPartner?.GeoMarketShort?.trim() || cachedConfiguration?.geoMarketShort?.trim()

  useEffect(() => {
    if (!partnerId || !fYear || cachedMarketShort) {
      return
    }

    let isCancelled = false

    fetchPartner360Configuration({ partnerId, fYear })
      .then(map621ToConfigurationData)
      .then((configuration) => {
        if (!isCancelled) {
          setRequestedConfiguration(configuration)
        }
      })
      .catch((error: Error) => {
        if (!isCancelled) {
          Logger.error(`Failed to load KI business priorities market: ${error.message}`)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [partnerId, fYear, cachedMarketShort])

  if (!partnerId || !fYear) {
    return null
  }

  // Short codes match KIBusinessPriorities.Markets. Full names retain the legacy fallback.
  return (
    cachedMarketShort ||
    loadedConfiguration?.geoMarketShort?.trim() ||
    loadedConfiguration?.geoMarket ||
    cockpitPartner?.GeoMarket?.trim() ||
    cachedConfiguration?.geoMarket ||
    null
  )
}
